"use client";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import Dialog from "../../components/Dialog";
import ModalConfirmDialog from "../../components/ModalConfirmDialog";
import CustomTooltip from "../../components/CustomTooltip";
import { renderToast } from "../../components/renderToast";
import Spinner from "../../components/Spinner";
import { SupabaseProvider } from "../../components/SupabaseProvider";
import {
  REFERRAL_CODE_KEY,
  REFERRAL_QUERY_PARAM,
} from "../../const/referral";
import {
  SS_CURATION_KEY,
  SS_CURATION_QUERY_PARAM,
} from "../../const/curation";
import {
  EXPERIMENTAL_KEY,
  EXPERIMENTAL_QUERY_PARAM,
} from "../../const/experimental";
import { MarketingPageContext } from "../../context/marketingPageContext";
import { AttributionHelper } from "../../helpers/AttributionHelper";
import { ModalContext, useModal } from "../../hooks/useModal";
import { usePostHogIdentify } from "../../hooks/usePostHogIdentify";

// Captures the `?r=<code>` referral code into localStorage and the UTM /
// click-ID attribution data into the standard attribution storage so
// downstream consumers (`components/SubmitVideoForm.tsx` for `?r=`, and
// `app/(app)/success/page.tsx` for the rest) can attribute the signup.
// Marketing landing pages are the entry point for paid traffic, so this
// has to live here (not in `AppProviders`, which only mounts post-signin).
function ReferralCapture() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get(REFERRAL_QUERY_PARAM);
    if (code) {
      localStorage.setItem(REFERRAL_CODE_KEY, code);
      posthog.capture("Referral Code Found", {
        referral_code: code,
        $set: { referred: true },
      });
    }
    if (params.get(SS_CURATION_QUERY_PARAM) === "true") {
      localStorage.setItem(SS_CURATION_KEY, "true");
    }
    if (params.get(EXPERIMENTAL_QUERY_PARAM) === "true") {
      localStorage.setItem(EXPERIMENTAL_KEY, "true");
    }
    AttributionHelper.captureAttribution();
  }, []);
  return null;
}

// Fires `$pageview` on every pathname change. PostHog is configured below
// with `capture_pageview: false` so we own the event manually — that way
// the very first navigation after `posthog.init` also gets captured (the
// SDK's built-in pageview hook misses the initial route in App Router).
function RouteTracking() {
  const pathname = usePathname();
  useEffect(() => {
    posthog.capture("$pageview");
  }, [pathname]);
  return null;
}

// Identifies signed-in visitors (the app tree does this in AuthRedirect),
// including one who just signed up in the signup dialog on this page.
function PostHogIdentify() {
  usePostHogIdentify({ onlyIfAnonymous: true });
  return null;
}

// Provider tree for marketing pages. We accept the ~175 KB cost of
// initialising PostHog here in exchange for marketing-funnel analytics:
// every $pageview on `/`, `/pricing`, `/alternatives/*`, `/blog/*`, etc.
// flows through here, and `posthog.capture(...)` calls inside marketing
// components (`Plan Clicked`, `Invalid YouTube URL`, "Referral Code Found",
// etc.) actually fire.
//
// Kept lean vs. `AppProviders`:
//   - No OneSignal (push notifications, not relevant pre-signin)
//   - No QueryParamProvider (the `next-query-params/app` adapter calls
//     `useSearchParams()`, which forces a Suspense boundary in the layout;
//     Next.js then streams metadata into the body and breaks the SEO
//     `meta-description` audit. Marketing components have been migrated
//     off `useQueryParam` to `window.location.search` reads.)
//   - No GlobalErrorBoundary, ExportProvider, AuthRedirect, FreeTrialBanner,
//     SubscriptionIssueBanner — none needed for marketing.
//   - CustomTooltip is included so pricing-card ⓘ tooltips work on /pricing.
//
// Includes ModalContext + Dialog + Toaster so `VideoInput`'s "paste a
// YouTube URL" flow works on the homepage and alternatives pages: signed-in
// visitors get the full submit modal, signed-out visitors get the signup
// dialog first (components/SignupDialog.tsx), then the submit modal.
// Initialize at module scope (not in a useEffect) so feature flags persisted
// in localStorage from a previous visit are available synchronously on the
// very first render. Initializing in an effect ran *after* child components
// mounted, so flag-driven UI rendered the default layout first and flickered
// to the variant.
if (typeof window !== "undefined") {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
    api_host: "https://klap.app/ingest",
    autocapture: false,
    capture_pageview: false,
    loaded: (ph) => {
      if (process.env.NODE_ENV === "development") {
        ph.debug();
        // Console access for local flag testing, e.g.:
        //   posthog.featureFlags.overrideFeatureFlags({ flags: { "flag-key": "variant" } })
        //   posthog.featureFlags.overrideFeatureFlags(false) // clear
        (window as any).posthog = ph;
      }
    },
  });
}

export default function MarketingProviders({
  children,
}: {
  children: React.ReactNode;
}) {
  const [queryClient] = useState(() => new QueryClient());
  const modal = useModal();

  return (
    <QueryClientProvider client={queryClient}>
      <PostHogProvider client={posthog}>
        <SupabaseProvider>
          <RouteTracking />
          <ReferralCapture />
          <PostHogIdentify />
          <ModalContext.Provider value={modal}>
            <MarketingPageContext.Provider value={true}>
              {children}
            </MarketingPageContext.Provider>
            <Toaster
              containerClassName="!z-[99999]"
              toastOptions={{
                loading: {
                  icon: <Spinner className="w-4 h-4 shrink-0" />,
                },
                success: {
                  icon: <CheckIcon className="w-4 h-4 shrink-0" />,
                },
                error: {
                  icon: <XMarkIcon className="w-4 h-4 shrink-0" />,
                },
                className:
                  "!rounded-xl !bg-slate-950 !text-white !rounded-xl shadow-2xl !shadow-slate-950/20 text-base font-normal font-sans border border-slate-800  !text-slate-200 text-sm dark",
              }}
            >
              {renderToast}
            </Toaster>
            <Dialog
              ref={modal.ref}
              isOpen={modal.isOpen}
              onClose={() => {
                // Held while the signup dialog waits on its Google popup
                // (useModal.holdRef): Escape, the overlay and the X do
                // nothing until it's done.
                if (modal.holdRef.current) return;
                modal.close(modal.confirmRef.current);
              }}
              className={modal.className}
              disabledPadding={modal.disabledPadding}
            >
              {modal.children}
            </Dialog>
            <ModalConfirmDialog />
            <CustomTooltip id="global" />
          </ModalContext.Provider>
        </SupabaseProvider>
      </PostHogProvider>
    </QueryClientProvider>
  );
}
